import {queryOptions, useMutation, useQueryClient} from '@tanstack/react-query';
import {api, apiJson, ApiError} from './client';

export interface SessionFolder {
  anketaId: number;
  anketaName: string;
  driveFolderId: string;
}

export interface MeResponse {
  telegramId: number;
  usernameLocked: string;
  firstName: string | null;
  isAdmin: boolean;
  folders: SessionFolder[];
}

export const meQueryOptions = queryOptions({
  queryKey: ['me'],
  queryFn: () => api<MeResponse>('/auth/me'),
  retry: (failureCount, err) => {
    if (err instanceof ApiError && err.status === 401) return false;
    return failureCount < 1;
  },
  staleTime: 5 * 60 * 1000,
});

export function useWebAppLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (initData: string) => apiJson<MeResponse>('/auth/webapp', {initData}),
    onSuccess: (data) => {
      qc.setQueryData(meQueryOptions.queryKey, data);
    },
  });
}

export interface LoginWidgetPayload {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

export function useTelegramLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: LoginWidgetPayload) =>
      apiJson<MeResponse>('/auth/telegram', payload),
    onSuccess: (data) => {
      qc.setQueryData(meQueryOptions.queryKey, data);
    },
  });
}

export interface TicketStartResponse {
  ticket: string;
  botDeepLink: string;
  expiresAt: string;
}

export function useStartLoginTicket() {
  return useMutation({
    mutationFn: () => apiJson<TicketStartResponse>('/auth/ticket/start', {}),
  });
}

export function useExchangeLoginToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      apiJson<MeResponse>('/auth/token/exchange', {token}),
    onSuccess: (data) => {
      qc.setQueryData(meQueryOptions.queryKey, data);
    },
  });
}

export async function waitForTicketApproval(
  ticket: string,
  signal: AbortSignal,
): Promise<MeResponse> {
  while (!signal.aborted) {
    try {
      return await api<MeResponse>(`/auth/ticket/${ticket}/wait`, {signal});
    } catch (err) {
      if (signal.aborted) throw err;
      if (err instanceof ApiError && err.status === 408) continue;
      throw err;
    }
  }
  throw new DOMException('aborted', 'AbortError');
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiJson<{ok: true}>('/auth/logout', {}),
    onSettled: () => {
      qc.removeQueries({queryKey: meQueryOptions.queryKey});
      qc.clear();
    },
  });
}
